import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3, Bot, CheckCircle2, Clock, Download, Inbox, Mail, MessageCircle, ShieldAlert, Smile, TriangleAlert, Users } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { hd, errorMessage } from "./api";
import { CHART, CardHeader, ChartTooltip, GlassCard, PageHeader, SERIES, StatRow, StatTile, T, TYPE, formatDuration, ghostButtonSx, labelSx } from "../user/complaints/shared";

const TABS = [
  ["dashboard", "Dashboard"], ["tickets", "Tickets"], ["agents", "Agents"], ["teams", "Teams"], ["sla", "SLA"], ["csat", "CSAT"],
  ["email", "Email"], ["chat", "Chat"], ["ai", "AI"],
];
const pctText = (v) => (v == null ? "—" : `${v}%`);
const minsText = (v) => (v == null ? "—" : formatDuration(v));
const iso = (d) => d.toISOString().slice(0, 10);

function Reports({ initial = "dashboard" }) {
  const ctx = useHd();
  const [params, setParams] = useSearchParams();
  const tab = params.get("r") || initial;
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    // keep the report name with its data so a tab switch never renders the
    // previous report's data with the new report's layout
    hd.report(tab, { from, to }).then((d) => live && (setData({ name: tab, d }), setError(""))).catch((err) => live && setError(errorMessage(err)));
    return () => {
      live = false;
    };
  }, [tab, from, to]);

  const exportCsv = async () => {
    try {
      const blob = await hd.reportCsv(tab, { from, to });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `helpdesk-${tab}-${from}-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  return (
    <Box>
      <PageHeader
        icon={BarChart3}
        title={tab === "dashboard" ? "Support Dashboard" : "Support Reports"}
        subtitle="Real-time customer support performance metrics, SLA compliance, response times, and channel analytics"
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
            {ctx.can("reports.export") && <Button variant="outlined" sx={ghostButtonSx} startIcon={<Download size={15} />} onClick={exportCsv}>CSV</Button>}
          </Stack>
        } />
      <Tabs value={tab} onChange={(_, v) => setParams({ r: v })} variant="scrollable" sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}>
        {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
      </Tabs>
      {error && <Alert severity="error">{error}</Alert>}
      {(!data || data.name !== tab) && !error && <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>}
      {data && data.name === tab && <ReportBody name={tab} d={data.d} />}
    </Box>
  );
}

const SimpleTable = ({ cols, rows = [] }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead><TableRow sx={{ "& th": { ...labelSx, bgcolor: T.surfaceHeader } }}>{cols.map(([k, l]) => <TableCell key={k}>{l}</TableCell>)}</TableRow></TableHead>
      <TableBody>
        {rows.map((r, i) => <TableRow key={i}>{cols.map(([k, , fmt]) => <TableCell key={k} sx={{ fontSize: TYPE.small }}>{fmt ? fmt(r[k], r) : r[k] ?? "—"}</TableCell>)}</TableRow>)}
        {!rows.length && <TableRow><TableCell colSpan={cols.length} sx={{ color: T.inkMuted }}>No data in this range</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Box>
);

const ChartCard = ({ title, children, height = 260 }) => (
  <GlassCard sx={{ mb: 2 }}>
    <CardHeader title={title} />
    <Box sx={{ p: 2, height }}><ResponsiveContainer>{children}</ResponsiveContainer></Box>
  </GlassCard>
);

function ReportBody({ name, d }) {
  switch (name) {
    case "dashboard":
      return (
        <>
          <StatRow columns={6}>
            <StatTile label="Total tickets" value={d.totals.total} icon={Inbox} />
            <StatTile label="Open" value={d.totals.open} icon={Inbox} tone="info" />
            <StatTile label="Pending" value={d.totals.pending} icon={Clock} tone="warning" />
            <StatTile label="Resolved" value={d.totals.resolved} icon={CheckCircle2} tone="good" />
            <StatTile label="Closed" value={d.totals.closed} icon={CheckCircle2} />
            <StatTile label="Unassigned" value={d.totals.unassigned} icon={Users} tone={d.totals.unassigned ? "warning" : "good"} />
            <StatTile label="SLA breached" value={d.totals.slaBreached} icon={TriangleAlert} tone={d.totals.slaBreached ? "critical" : "good"} />
            <StatTile label="Avg 1st response" value={minsText(d.avgFirstResponseMins)} icon={Clock} />
            <StatTile label="Avg resolution" value={minsText(d.avgResolutionMins)} icon={Clock} />
            <StatTile label="CSAT" value={d.csat.average ?? "—"} sub={`${d.csat.responses} responses`} icon={Smile} tone="good" />
            <StatTile label="AI resolutions" value={d.aiResolutions} icon={Bot} tone="info" />
            <StatTile label="Human resolutions" value={d.humanResolutions} icon={Users} />
            <StatTile label="AI → human" value={d.aiToHumanHandoffs} icon={Bot} tone="warning" />
            <StatTile label="Chat volume" value={d.chatVolume} icon={MessageCircle} />
            <StatTile label="Email volume" value={d.emailVolume} icon={Mail} />
            <StatTile label="Escalated" value={d.totals.escalated} icon={ShieldAlert} tone={d.totals.escalated ? "critical" : "good"} />
          </StatRow>
          <ChartCard title="Created vs resolved">
            <LineChart data={d.trend}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="date" tick={CHART.tick} {...CHART.axisProps} />
              <YAxis allowDecimals={false} tick={CHART.tick} {...CHART.axisProps} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="created" name="Created" stroke={SERIES[0]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="resolved" name="Resolved" stroke={SERIES[2]} strokeWidth={2} dot={false} />
            </LineChart>
          </ChartCard>
        </>
      );
    case "tickets":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
          {[["byStatus", "By status"], ["byPriority", "By priority"], ["byChannel", "By channel"], ["byCategory", "By category"], ["byHandler", "AI vs human"]].map(([k, l]) => (
            <ChartCard key={k} title={l} height={240}>
              <BarChart data={d[k]} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid stroke={CHART.grid} horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={CHART.tick} {...CHART.axisProps} />
                <YAxis type="category" dataKey="name" tick={CHART.tick} width={120} {...CHART.axisProps} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Tickets" fill={SERIES[0]} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartCard>
          ))}
        </Box>
      );
    case "agents":
    case "teams":
      return (
        <GlassCard>
          <SimpleTable rows={d.rows} cols={[["name", name === "agents" ? "Agent" : "Team"], ["assigned", "Created"], ["resolved", "Resolved"], ["open", "Open now"], ["avgFirstResponseMins", "Avg 1st response", minsText], ["avgResolutionMins", "Avg resolution", minsText], ["slaBreached", "SLA breached"], ["csatAverage", "CSAT", (v, r) => (v == null ? "—" : `${v} (${r.csatResponses})`)]]} />
        </GlassCard>
      );
    case "sla":
      return (
        <>
          <StatRow columns={2}>
            <StatTile label="First response compliance" value={pctText(d.overall.firstResponseCompliancePct)} tone="good" />
            <StatTile label="Resolution compliance" value={pctText(d.overall.resolutionCompliancePct)} tone="good" />
          </StatRow>
          <GlassCard>
            <SimpleTable rows={d.byPriority.map((r) => ({ priority: r.priority, total: r.total, frMet: r.firstResponse.met, frB: r.firstResponse.breached, frPct: r.firstResponse.compliancePct, resMet: r.resolution.met, resB: r.resolution.breached, resPct: r.resolution.compliancePct }))}
              cols={[["priority", "Priority"], ["total", "Tickets"], ["frMet", "1st response met"], ["frB", "1st response breached"], ["frPct", "Compliance", pctText], ["resMet", "Resolution met"], ["resB", "Resolution breached"], ["resPct", "Compliance", pctText]]} />
          </GlassCard>
        </>
      );
    case "csat":
      return (
        <>
          <StatRow columns={4}>
            <StatTile label="Average" value={d.average ?? "—"} tone="good" />
            <StatTile label="Responses" value={d.responses} />
            <StatTile label="Satisfied (4–5★)" value={pctText(d.satisfiedPct)} tone="good" />
            <StatTile label="Response rate" value={pctText(d.responseRatePct)} sub={`${d.surveysSent} surveys sent`} />
          </StatRow>
          <ChartCard title="Rating distribution" height={220}>
            <BarChart data={d.distribution}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="rating" tick={CHART.tick} {...CHART.axisProps} />
              <YAxis allowDecimals={false} tick={CHART.tick} {...CHART.axisProps} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" name="Responses" fill={SERIES[3]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <GlassCard><CardHeader title="By agent" /><SimpleTable rows={d.byAgent} cols={[["name", "Agent"], ["average", "Average"], ["responses", "Responses"]]} /></GlassCard>
            <GlassCard><CardHeader title="Recent feedback" /><SimpleTable rows={d.recentFeedback} cols={[["ticketKey", "Ticket"], ["rating", "Rating"], ["feedback", "Feedback"]]} /></GlassCard>
          </Box>
        </>
      );
    case "email":
      return (
        <StatRow columns={4}>
          <StatTile label="Inbound emails" value={d.inbound.total} icon={Mail} />
          <StatTile label="Processed" value={d.inbound.done || 0} tone="good" />
          <StatTile label="Ignored / spam" value={(d.inbound.ignored || 0) + (d.inbound.spam || 0)} tone="warning" />
          <StatTile label="Failed" value={d.inbound.failed || 0} tone={d.inbound.failed ? "critical" : "good"} />
          <StatTile label="Outbound emails" value={d.outbound.total} icon={Mail} />
          <StatTile label="Sent" value={d.outbound.sent || 0} tone="good" />
          <StatTile label="Send failed" value={d.outbound.failed || 0} tone={d.outbound.failed ? "critical" : "good"} />
          <StatTile label="Tickets from email" value={d.ticketsCreatedFromEmail} />
        </StatRow>
      );
    case "chat":
      return (
        <StatRow columns={4}>
          <StatTile label="Conversations" value={d.conversations} icon={MessageCircle} />
          <StatTile label="Handled by AI first" value={d.aiEngaged} icon={Bot} />
          <StatTile label="Handoffs to humans" value={d.handoffs} tone="warning" />
          <StatTile label="Accepted by agents" value={d.acceptedByAgents} tone="good" />
          <StatTile label="Missed" value={d.missed} tone={d.missed ? "critical" : "good"} />
          <StatTile label="Transfers" value={d.transfers} />
          <StatTile label="Avg queue wait" value={minsText(d.avgQueueWaitMins)} />
          <StatTile label="Avg duration" value={minsText(d.avgDurationMins)} />
          <StatTile label="Waiting now" value={d.waitingNow} tone={d.waitingNow ? "warning" : "good"} />
        </StatRow>
      );
    case "ai":
      return (
        <>
          <StatRow columns={5}>
            <StatTile label="AI conversations" value={d.aiConversations} icon={Bot} />
            <StatTile label="AI resolved" value={d.aiResolved} sub={`rate ${pctText(d.aiResolutionRatePct)}`} tone="good" />
            <StatTile label="Handoff rate" value={pctText(d.handoffRatePct)} sub={`${d.handoffs} handoffs`} tone="warning" />
            <StatTile label="Failure rate" value={pctText(d.failureRatePct)} sub={`${d.botReplies} bot replies`} tone={d.failureRatePct > 30 ? "critical" : "neutral"} />
            <StatTile label="Avg AI response" value={d.avgBotLatencyMs == null ? "—" : `${(d.avgBotLatencyMs / 1000).toFixed(1)}s`} />
            <StatTile label="Human takeovers" value={d.humanTakeovers} />
            <StatTile label="CSAT (AI handled)" value={d.csatAiHandled.average ?? "—"} sub={`${d.csatAiHandled.responses} responses`} />
          </StatRow>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <GlassCard><CardHeader title="Handoff reasons" /><SimpleTable rows={d.handoffReasons} cols={[["reason", "Reason", (v) => String(v).replace(/_/g, " ")], ["count", "Conversations"]]} /></GlassCard>
            <GlassCard><CardHeader title="AI usage" /><SimpleTable rows={d.usage} cols={[["kind", "Feature"], ["outcome", "Outcome"], ["count", "Calls"], ["tokens", "Tokens"]]} /></GlassCard>
          </Box>
          <GlassCard sx={{ p: 2, mt: 2 }}>
            {Object.entries(d.definitions).map(([k, v]) => <Typography key={k} sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}><b>{k}</b>: {v}</Typography>)}
          </GlassCard>
        </>
      );
    default:
      return null;
  }
}

export default function SupportReports({ initial }) {
  return <HelpdeskPage><Reports initial={initial} /></HelpdeskPage>;
}

export const SupportDashboard = () => <SupportReports initial="dashboard" />;
